<script lang="ts">
	// Svelte functions
	import { createEventDispatcher } from 'svelte';
	// Types
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { toast } from '@Shelly/shelly-design-system';
	// Components
	import VideoActions from '../CameraPlayer/VideoActions.svelte';
	import VideoActionButton from '../CameraPlayer/VideoActionButton.svelte';
	import { getActiveStreamRecording, getCameraComponentKey } from '../helpers/camera-helpers';
	import type { CameraRecordings } from '../types/camera-status';
	import { PttUiState } from '../types/ptt-ui-state';

	const dispatch = createEventDispatcher<{ talkToggle: boolean }>();

	export let device: Device;
	export let status: DeviceStatusG2;
	export let settings: DeviceSettingsG2;
	export let pttState: PttUiState = PttUiState.UNAVAILABLE;
	export let streamLoading: boolean = false;

	// State variables
	let isRecordLoading = false;
	let isPhotoLoading = false;
	let photoSaved = false;
	let isArmLoading = false;
	let isPrivacyLoading = false;

	// Reactive values from device status/settings
	$: channel = device?.channel || 0;
	$: cameraKey = getCameraComponentKey(device);
	$: cameraSettings = settings?.[cameraKey];
	$: isArmed = cameraSettings?.arm ?? false;
	$: isPrivacyMode = cameraSettings?.privacy ?? false;
	// Talkback usability is driven by the PTT channel state itself, not a
	// stream-liveness proxy: `unavailable` (no channel yet) and `closed`
	// (channel dropped) are the only blocking states.
	$: isTalkDisabled =
		streamLoading || pttState === PttUiState.UNAVAILABLE || pttState === PttUiState.CLOSED;
	$: recordings = status?.[cameraKey]?.recordings as CameraRecordings | undefined;
	$: activeRecording = getActiveStreamRecording(recordings);
	$: isRecording = !!activeRecording;

	const handlePhoto = async () => {
		isPhotoLoading = true;
		const res = await gen2Request(device, 'Camera.CaptureImage', { id: channel, stream: 0 });
		isPhotoLoading = false;
		if (res?.isok) {
			photoSaved = true;
			// Reset after showing success
			await new Promise((resolve) => setTimeout(resolve, 2000));
			photoSaved = false;
		} else {
			toast.open({
				type: 'danger',
				title: 'Failed to capture image',
			});
		}
	};

	const handleRecord = async () => {
		isRecordLoading = true;
		if (isRecording && activeRecording?.id) {
			const res = await gen2Request(device, 'Camera.StopRecording', {
				id: channel,
				rec_id: activeRecording.id,
			});
			if (!res?.isok) {
				toast.open({
					type: 'danger',
					title: 'Failed to stop recording',
				});
			}
		} else {
			const res = await gen2Request(device, 'Camera.StartRecording', { id: channel });
			if (!res?.isok) {
				toast.open({
					type: 'danger',
					title: 'Failed to start recording',
				});
			}
		}
		isRecordLoading = false;
	};

	const handleArm = async () => {
		isArmLoading = true;
		const res = await gen2Request(device, 'Camera.SetConfig', {
			id: channel,
			config: { arm: !isArmed },
		});
		isArmLoading = false;
		if (res?.isok) {
			settings[cameraKey].arm = !isArmed;
		} else {
			toast.open({
				type: 'danger',
				title: isArmed ? 'Failed to disarm' : 'Failed to arm',
			});
		}
	};

	const handleTalkToggle = () => {
		if (pttState === PttUiState.REQUESTING || isTalkDisabled) return;
		// Click only emits intent; the parent decides when to flip `pttState`.
		dispatch('talkToggle', pttState !== PttUiState.GRANTED);
	};

	const handlePrivacy = async () => {
		isPrivacyLoading = true;
		const res = await gen2Request(device, 'Camera.SetConfig', {
			id: channel,
			config: { privacy: !isPrivacyMode },
		});
		isPrivacyLoading = false;
		if (res?.isok) {
			settings[cameraKey].privacy = !isPrivacyMode;
		} else {
			toast.open({
				type: 'danger',
				title: isPrivacyMode
					? 'Failed to disable privacy mode'
					: 'Failed to enable privacy mode',
			});
		}
	};
</script>

<VideoActions>
	<VideoActionButton
		icon={photoSaved ? 'fas fa-circle-check' : 'fas fa-camera'}
		label={photoSaved ? 'Saved' : 'Photo'}
		loading={isPhotoLoading}
		disabled={isPrivacyMode}
		variant={photoSaved ? 'success' : 'default'}
		on:click={handlePhoto}
	/>
	<VideoActionButton
		icon={isRecording ? 'fas fa-circle-stop' : 'fas fa-circle-dot'}
		label={isRecording ? 'Stop' : 'Record'}
		loading={isRecordLoading}
		disabled={isPrivacyMode}
		variant={isRecording ? 'danger' : 'default'}
		on:click={handleRecord}
	/>
	<VideoActionButton
		icon="fas fa-microphone"
		label="Talk"
		active={pttState === PttUiState.GRANTED}
		loading={pttState === PttUiState.REQUESTING}
		disabled={isTalkDisabled}
		variant={pttState === PttUiState.GRANTED ? 'warning' : 'default'}
		on:click={handleTalkToggle}
	/>
	<VideoActionButton
		icon={isArmed ? 'fas fa-shield' : 'fas fa-shield-halved'}
		label={isArmed ? 'Disarm' : 'Arm'}
		loading={isArmLoading}
		disabled={isPrivacyMode}
		variant={isArmed ? 'danger' : 'default'}
		on:click={handleArm}
	/>
	<VideoActionButton
		icon={isPrivacyMode ? 'fas fa-video' : 'fas fa-video-slash'}
		label={isPrivacyMode ? 'Show' : 'Privacy'}
		loading={isPrivacyLoading}
		variant={isPrivacyMode ? 'warning' : 'default'}
		on:click={handlePrivacy}
	/>
</VideoActions>
